import { ConstraintDocument, ConstraintMode } from 'types/ConstraintProfileTypes';
import { SAVABLE_PATHS, splitPath } from './constraintSchema';

/** Numeric tolerance — constraint values are floats and small drift is not a change. */
const EPSILON = 1e-6;

/** Deep copy of a Constraint Document. Documents are JSON-serializable by construction. */
export function cloneDocument(doc: ConstraintDocument): ConstraintDocument {
  return JSON.parse(JSON.stringify(doc ?? {}));
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < EPSILON;
  }
  if (a == null && b == null) {
    return true;
  }
  if (a != null && b != null && typeof a === 'object' && typeof b === 'object') {
    // Object/array-valued fields (e.g. microcategory_weights): compare by
    // JSON content. Known limitations: key-insertion order is significant
    // and floats nested inside bypass EPSILON. Acceptable for the dict-
    // valued snapshot fields currently in scope; revisit if a new dict-
    // valued field with floats lands in the savable schema.
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
}

/**
 * Return every `group.field` path where document `a` differs from `b`.
 *
 * Two comparison modes:
 *
 *   1. **Schema-driven (`mode` passed)**: iterate ONLY the savable paths
 *      declared in `constraintSchema.SAVABLE_PATHS[mode]` — the spec's
 *      "Snapshot: Yes" rows. Anything outside the schema (reference data
 *      like `risk.risk_limits`, SSO-static fields like `risk.risk_profile`
 *      in rebalancing, advisor-curated `product.*`, mode-irrelevant
 *      `liquidity.*` in builder, etc.) is invisible to comparison
 *      regardless of which value it carries on either side. This is the
 *      mode used by `isModified`, profile-card delta counts, and the Save
 *      payload filter — anywhere "did the user-visible constraints change"
 *      is being asked.
 *
 *   2. **Strict (no `mode`)**: compare every key present in either doc.
 *      Reserved for echo detection — the SDK echo carries back the exact
 *      payload we sent, so deep equality on the whole blob is correct.
 */
export function diffDocumentPaths(
  a: ConstraintDocument,
  b: ConstraintDocument,
  mode?: ConstraintMode,
): string[] {
  if (mode) {
    return diffSavablePaths(a, b, mode);
  }
  return diffStrict(a, b);
}

function diffSavablePaths(
  a: ConstraintDocument,
  b: ConstraintDocument,
  mode: ConstraintMode,
): string[] {
  const paths: string[] = [];
  SAVABLE_PATHS[mode].forEach((path) => {
    const [group, field] = splitPath(path);
    const ga = a?.[group];
    const gb = b?.[group];
    const va =
      ga != null && typeof ga === 'object'
        ? (ga as Record<string, unknown>)[field]
        : undefined;
    const vb =
      gb != null && typeof gb === 'object'
        ? (gb as Record<string, unknown>)[field]
        : undefined;
    // `undefined` means "this representation doesn't carry the field" —
    // either side being sparse for this path means we have no signal to
    // compare and the user hasn't actively set / cleared anything. `null`
    // is different (explicit "no value") and still gets compared below.
    if (va === undefined || vb === undefined) return;
    if (!valuesEqual(va, vb)) {
      paths.push(path);
    }
  });
  return paths;
}

/**
 * Paths to skip even in strict mode. Reserved for fields whose content
 * is reference data that the server may serialize with different key
 * order across requests, breaking JSON-string equality.
 */
const STRICT_NON_COMPARABLE_PATHS = new Set<string>(['risk.risk_limits']);

/**
 * Per-field diff for the `product` group, which is ISIN-keyed and thus
 * outside the static SAVABLE_PATHS schema.
 *
 * The product subtree lives at `doc.product.{section}.{ISIN}.{field}`
 * where `section` is one of `individual_products` / `list_products` /
 * `portfolio_products`, and `field` is one of the rebalanceable
 * per-product attributes (`weight_min`, `weight_max`, `value_min`,
 * `value_max`, `hold_mode`). Returns dotted paths of the form
 * `product.<section>.<isin>.<field>` for every cell where the value
 * differs between `a` and `b`.
 *
 * Used by `useConstraintsChangeDetection` to surface agent-driven
 * per-product changes into `constraintsStore.agentChangedPaths`, the
 * same array that drives the panel's orange borders. The product modal
 * reads that array directly — both surfaces share one source of truth.
 *
 * `selected_list_ids` (sibling of the per-ISIN structures) is also
 * diffed; an agent toggling list selection appears as a path
 * `product.selected_list_ids`. The modal can show a one-row indicator
 * on the list-dropdown if/when we choose to surface it.
 */
const PRODUCT_FIELDS = ['weight_min', 'weight_max', 'value_min', 'value_max', 'hold_mode'] as const;
const PRODUCT_SECTIONS = ['individual_products', 'list_products', 'portfolio_products'] as const;

export function diffProductPaths(
  a: ConstraintDocument,
  b: ConstraintDocument,
): string[] {
  const paths: string[] = [];
  const productA = (a?.product ?? {}) as Record<string, unknown>;
  const productB = (b?.product ?? {}) as Record<string, unknown>;

  for (const section of PRODUCT_SECTIONS) {
    const sectionA = (productA[section] ?? {}) as Record<string, Record<string, unknown>>;
    const sectionB = (productB[section] ?? {}) as Record<string, Record<string, unknown>>;
    const isinUnion = [...Object.keys(sectionA), ...Object.keys(sectionB)];
    const isins = Array.from(new Set(isinUnion));
    for (const isin of isins) {
      const itemA = sectionA[isin] ?? {};
      const itemB = sectionB[isin] ?? {};
      // Per-field diff covers row-add (itemA = {}, sparse itemB), row-modify
      // (both populated), and row-remove (sparse itemA, itemB = {}) in one
      // pass. For a row-add with a sparse payload like `{weight_max: 0.32}`,
      // only `weight_max` diffs against undefined and emits a path; absent
      // fields stay undefined-vs-undefined and produce no false positives.
      // This is what makes "agent set this one value on a new row" show
      // orange on exactly that cell.
      for (const field of PRODUCT_FIELDS) {
        if (!valuesEqual(itemA[field], itemB[field])) {
          paths.push(`product.${section}.${isin}.${field}`);
        }
      }
    }
  }

  if (!valuesEqual(productA.selected_list_ids, productB.selected_list_ids)) {
    paths.push('product.selected_list_ids');
  }

  return paths;
}

/**
 * Per-field diff for the `microcategoria_advice.microcategory_weights`
 * subtree. Microcategory weights are keyed by category id (analogous to
 * the ISIN-keyed product subtree) and therefore live outside SAVABLE_PATHS.
 *
 * Path shape matches what `InputConstraints.tsx` (via `GridItemWithRemove`
 * in `MicroCategoryContent`) looks up at render time:
 *   `microcategoria_advice.<categoryId>_min`
 *   `microcategoria_advice.<categoryId>_max`
 * NOT the storage shape `microcategoria_advice.microcategory_weights.<id>.min`.
 * `MicroCategoryContent` passes the field id as `<categoryId>_min` /
 * `<categoryId>_max` to InputConstraints, which then composes the lookup
 * path as `${sectionKey}.${id}` — i.e. the shape this function emits.
 *
 * Row-add semantics mirror `diffProductPaths`: for a newly-introduced
 * category, `itemA = {}`, so only the populated side's cells produce
 * paths (sparse modify against an empty prior produces exactly the
 * fields the agent populated).
 */
export function diffMicrocategoryPaths(
  a: ConstraintDocument,
  b: ConstraintDocument,
): string[] {
  const paths: string[] = [];
  const wA = ((a?.microcategoria_advice as Record<string, unknown> | undefined)
    ?.microcategory_weights ?? {}) as Record<string, { min?: unknown; max?: unknown }>;
  const wB = ((b?.microcategoria_advice as Record<string, unknown> | undefined)
    ?.microcategory_weights ?? {}) as Record<string, { min?: unknown; max?: unknown }>;
  const idUnion = [...Object.keys(wA), ...Object.keys(wB)];
  const ids = Array.from(new Set(idUnion));
  for (const id of ids) {
    const itemA = wA[id] ?? {};
    const itemB = wB[id] ?? {};
    if (!valuesEqual(itemA.min, itemB.min)) {
      paths.push(`microcategoria_advice.${id}_min`);
    }
    if (!valuesEqual(itemA.max, itemB.max)) {
      paths.push(`microcategoria_advice.${id}_max`);
    }
  }
  return paths;
}

/**
 * Per-thread fields that live in the working document but are NOT part of
 * any profile's savable snapshot (they're outside SAVABLE_PATHS). When the
 * user switches profile, applies Predefiniti, or hits Reset, the new
 * baseline brings no values for these paths — without preservation the
 * advisor's curated product picks would silently vanish.
 *
 * Only the `product.*` subtree (advisor-curated preferred products, lists,
 * product groups and the imported portfolio holdings) is preserved across
 * profile switch / Predefiniti / Reset, in BOTH modes. Liquidity used to be preserved too,
 * but client feedback flagged the carry-over as a bug: a Prelievo / Apporto
 * the advisor entered would stay visible on the FE even after Reset or
 * after applying a different profile, despite never being saved. Liquidity
 * now resets alongside scalar fields; products remain preserved.
 */
const PRESERVED_FIELDS: ReadonlyArray<[string, string]> = [
  ['product', 'individual_products'],
  ['product', 'list_products'],
  ['product', 'selected_list_ids'],
  ['product', 'portfolio_products'],
];

export function preservePortfolioFields(
  current: ConstraintDocument | null,
  incoming: ConstraintDocument,
  _mode: ConstraintMode,
): ConstraintDocument {
  if (!current) return incoming;
  const result: ConstraintDocument = { ...incoming };
  for (const [group, field] of PRESERVED_FIELDS) {
    const currentGroup = current[group] as Record<string, unknown> | undefined;
    if (currentGroup && field in currentGroup) {
      const incomingGroup = (result[group] ?? {}) as Record<string, unknown>;
      result[group] = { ...incomingGroup, [field]: currentGroup[field] };
    }
  }
  return result;
}

/**
 * Overlay groups that a saved profile snapshot does not carry at all with the
 * mode's predefiniti value. A profile stored before a constraint group existed
 * (e.g. `wrapper`, schema version 1) has no key for it; without this the panel
 * renders that accordion blank instead of its defaults. Only `undefined`
 * groups are filled — an explicit `null` (mode-disabled group) is kept — and
 * only from non-null predefiniti groups.
 */
export function fillMissingGroups(
  doc: ConstraintDocument,
  predefiniti: ConstraintDocument | null,
): ConstraintDocument {
  if (!predefiniti) return doc;
  const result: ConstraintDocument = { ...doc };
  for (const [group, value] of Object.entries(predefiniti)) {
    if (value != null && result[group] === undefined) {
      result[group] = cloneDocument({ [group]: value })[group];
    }
  }
  return result;
}

function diffStrict(a: ConstraintDocument, b: ConstraintDocument): string[] {
  const paths: string[] = [];
  const groups = Array.from(
    new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]),
  );
  for (const group of groups) {
    const ga = a?.[group];
    const gb = b?.[group];
    if (ga == null || gb == null) {
      if (ga == null && gb == null) continue;
      paths.push(group);
      continue;
    }
    const fields = Array.from(new Set([...Object.keys(ga), ...Object.keys(gb)]));
    for (const field of fields) {
      const path = `${group}.${field}`;
      if (STRICT_NON_COMPARABLE_PATHS.has(path)) continue;
      if (!valuesEqual(ga[field], gb[field])) {
        paths.push(path);
      }
    }
  }
  return paths;
}

/** True when two Constraint Documents are equal (deep, numeric epsilon). */
export function documentsEqual(
  a: ConstraintDocument,
  b: ConstraintDocument,
  mode?: ConstraintMode,
): boolean {
  return diffDocumentPaths(a, b, mode).length === 0;
}

/**
 * Plan-indexed VaR limits table (`{ plan_str: { min, max_verde, ... } }`).
 * Backed by `predefiniti.risk.risk_limits` or
 * `predefinitiReference.value_at_risk_limits`. Both have the same shape.
 */
export type VarLimitsByPlan = Record<
  string,
  { min?: number | null; max_verde?: number | null } & Record<string, unknown>
>;

/**
 * Collapse rebalancing-mode "off-state" expressions into a single canonical
 * form so the schema-driven diff treats them as equal. Three fields each
 * have two equivalent representations:
 *
 *   - `risk.align_to_plan`: `null` and `false` both mean "off".
 *   - `risk.target_var_min`: `null` and the plan's verde `min` both mean
 *     "no advisor override — agent uses Scenario 1 = plan default".
 *   - `risk.target_var_max`: `null` and the plan's verde `max_verde` both
 *     mean the same — Scenario 1 default.
 *
 * The agent's rebalancing payload computation
 * (`fineco_skills.py:4297-4329`) reads these and falls into "use plan
 * verde range" for either form, so they are behaviourally identical.
 * Without this normalisation, a profile that saved `null` and a working
 * doc that holds the plan's verde value diff at the field level even
 * though they represent the same state — flashing `(modificato)` after
 * a check/uncheck of the "Allinea al piano" toggle.
 *
 * The plan is read from each side's `risk.financial_plan`; the limits are
 * looked up in `limitsByPlan` (shared across both sides — the table is
 * static and plan-keyed internally). Each side normalises to its OWN
 * plan, so a profile with `financial_plan = 5` compared against a
 * working doc with `financial_plan = 4` correctly normalises to plans 5
 * and 4 respectively. Returns the input unchanged when not in rebalancing
 * mode, when `risk` is missing, or when the plan is unknown.
 */
export function normalizeRebalancingRiskForDiff(
  doc: ConstraintDocument,
  limitsByPlan: VarLimitsByPlan | null | undefined,
): ConstraintDocument {
  if (!doc || !limitsByPlan) return doc;
  const risk = (doc.risk ?? null) as Record<string, unknown> | null;
  if (risk == null || typeof risk !== 'object') return doc;
  const plan = risk.financial_plan;
  if (typeof plan !== 'number') return doc;
  const limits = limitsByPlan[String(plan)];
  if (!limits) return doc;
  return {
    ...doc,
    risk: {
      ...risk,
      align_to_plan: risk.align_to_plan ?? false,
      target_var_min:
        risk.target_var_min ?? (limits.min ?? null),
      target_var_max:
        risk.target_var_max ?? (limits.max_verde ?? null),
    },
  };
}

/**
 * Project a document to only its savable-per-mode fields, with every
 * field present (missing fields become `null`). Use this for:
 *
 *   - the Salva payload: the saved profile is then canonical — same shape
 *     across modes, no junk fields, no mystery omissions.
 *   - the applied-profile baseline snapshot: comparing working against
 *     this is then trivially shape-aligned.
 */
export function projectToSavable(
  doc: ConstraintDocument,
  mode: ConstraintMode,
): ConstraintDocument {
  const result: ConstraintDocument = {};
  SAVABLE_PATHS[mode].forEach((path) => {
    const [group, field] = splitPath(path);
    if (result[group] == null) {
      result[group] = {} as Record<string, unknown>;
    }
    const groupObj = result[group] as Record<string, unknown>;
    const sourceGroup = doc?.[group];
    const value =
      sourceGroup != null && typeof sourceGroup === 'object'
        ? (sourceGroup as Record<string, unknown>)[field]
        : undefined;
    groupObj[field] = value === undefined ? null : value;
  });
  return result;
}
